import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  adminStats, adminUsers, adminUpdateUser,
  adminGigs, adminUpdateGig,
  adminOrders, adminRefundOrder,
  adminWithdrawals, adminUpdateWithdrawal,
  adminReviews, adminDeleteReview,
  fetchAnalytics,
  adminListVerifications, adminDecideVerification,
  adminListAnnouncements, adminCreateAnnouncement, adminDeleteAnnouncement,
  adminListDisputes, adminResolveDispute,
  fileUrl,
} from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, Package, ShoppingBag, Wallet, MessageSquare, ShieldAlert, Ban, Check, Star, Trash2, DollarSign, BadgeCheck, BarChart3, Megaphone, X, AlertOctagon } from "lucide-react";
import DisputeChat from "@/components/DisputeChat";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) { toast.error("Admin only"); navigate("/"); return; }
    adminStats().then(setStats).catch(() => {});
  }, [loading, user, navigate]);

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <div className="border-b border-black/10 pb-6 mb-8 flex items-end justify-between">
        <div>
          <div className="overline text-primary mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4"/> ◆ ADMIN CONTROL</div>
          <h1 className="text-4xl font-bold tracking-tight">Noxi ops</h1>
        </div>
        <div className="mono text-xs text-muted-foreground">{user.email}</div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <Stat testid="admin-stat-users" icon={<Users className="w-5 h-5"/>} label="Users" value={stats.users_total} sub={`${stats.users_banned} banned`}/>
          <Stat testid="admin-stat-gmv" icon={<DollarSign className="w-5 h-5"/>} label="GMV" value={`$${stats.gmv.toLocaleString()}`} highlight/>
          <Stat testid="admin-stat-gigs" icon={<Package className="w-5 h-5"/>} label="Active gigs" value={`${stats.gigs_active}/${stats.gigs_total}`}/>
          <Stat testid="admin-stat-orders" icon={<ShoppingBag className="w-5 h-5"/>} label="Orders" value={stats.orders_total} sub={`${stats.disputes} cancelled`}/>
          <Stat testid="admin-stat-withdrawals" icon={<Wallet className="w-5 h-5"/>} label="Pending payouts" value={stats.withdrawals_pending}/>
          <Stat testid="admin-stat-reviews" icon={<Star className="w-5 h-5"/>} label="Reviews" value={stats.reviews_total}/>
          <Stat testid="admin-stat-disputes" icon={<AlertOctagon className="w-5 h-5"/>} label="Open disputes" value={stats.disputes_open || 0} sub={(stats.disputes_open || 0) > 0 ? "needs review" : "all clear"}/>
        </div>
      )}

      <Tabs defaultValue="analytics">
        <TabsList className="rounded-none border border-foreground/15 bg-transparent p-0 h-auto flex-wrap">
          {["analytics","users","gigs","orders","disputes","withdrawals","reviews","verifications","announcements"].map((t) => (
            <TabsTrigger key={t} data-testid={`admin-tab-${t}`} value={t} className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5 capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="analytics" className="pt-6"><AnalyticsPanel/></TabsContent>
        <TabsContent value="users" className="pt-6"><UsersPanel/></TabsContent>
        <TabsContent value="gigs" className="pt-6"><GigsPanel/></TabsContent>
        <TabsContent value="orders" className="pt-6"><OrdersPanel/></TabsContent>
        <TabsContent value="disputes" className="pt-6"><DisputesPanel/></TabsContent>
        <TabsContent value="withdrawals" className="pt-6"><WithdrawalsPanel/></TabsContent>
        <TabsContent value="reviews" className="pt-6"><ReviewsPanel/></TabsContent>
        <TabsContent value="verifications" className="pt-6"><VerificationsPanel/></TabsContent>
        <TabsContent value="announcements" className="pt-6"><AnnouncementsPanel/></TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon, label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className={`border p-5 ${highlight ? "border-primary bg-primary text-primary-foreground" : "border-foreground/15 bg-card"}`}>
      <div className={`flex items-center justify-between mb-3 ${highlight ? "opacity-80" : "text-muted-foreground"}`}>{icon}</div>
      <div className="mono text-2xl font-bold">{value}</div>
      <div className="overline mt-1">{label}</div>
      {sub && <div className={`text-xs mt-1 ${highlight ? "opacity-70" : "text-muted-foreground"}`}>{sub}</div>}
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const load = () => adminUsers(q).then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const toggleBan = async (u) => {
    try {
      await adminUpdateUser(u.user_id, { banned: !u.banned });
      toast.success(u.banned ? "Unbanned" : "Banned");
      load();
    } catch { toast.error("Failed"); }
  };

  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div>
      <Input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" className="mb-4 rounded-none max-w-md border-foreground/20"/>
      <div className="border border-foreground/15 overflow-x-auto">
        <div className="grid grid-cols-[1fr_180px_100px_100px_100px_140px] gap-4 px-4 py-3 border-b border-foreground/15 bg-muted overline min-w-[900px]">
          <div>◆ User</div><div>Email</div><div>Role</div><div>Profile</div><div>Rating</div><div>Action</div>
        </div>
        {users.map((u) => (
          <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="grid grid-cols-[1fr_180px_100px_100px_100px_140px] gap-4 px-4 py-3 border-b border-foreground/10 items-center text-sm min-w-[900px]">
            <div className="flex items-center gap-3 min-w-0">
              {u.picture ? <img src={u.picture} className="w-8 h-8 rounded-full object-cover" alt=""/> : <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">{u.name?.[0]}</div>}
              <span className="font-medium truncate">{u.name}</span>
              {u.banned && <span className="text-xs mono text-destructive">BANNED</span>}
            </div>
            <div className="mono text-xs truncate">{u.email}</div>
            <div className="mono text-xs uppercase">{u.role || "—"}</div>
            <div>
              <span className={`text-[10px] mono px-2 py-1 border ${u.profile_complete ? "border-primary text-primary" : "border-foreground/30 text-muted-foreground"}`}>
                {u.profile_complete ? "COMPLETE" : "INCOMPLETE"}
              </span>
            </div>
            <div className="mono text-xs">{u.rating_avg?.toFixed(1) || "—"} ({u.rating_count})</div>
            <div className="flex gap-2">
              <button
                data-testid={`admin-user-details-${u.user_id}`}
                onClick={() => setDetail(u)}
                className="mono text-xs px-2 py-1.5 border border-foreground/30 hover:bg-foreground hover:text-background"
              >DETAILS</button>
              <button
                data-testid={`admin-ban-${u.user_id}`}
                onClick={() => toggleBan(u)}
                className={`mono text-xs px-2 py-1.5 flex items-center gap-1 ${u.banned ? "border border-foreground hover:bg-foreground hover:text-background" : "border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"}`}
              >
                {u.banned ? <><Check className="w-3 h-3"/> UNBAN</> : <><Ban className="w-3 h-3"/> BAN</>}
              </button>
            </div>
          </div>
        ))}
        {users.length === 0 && <div className="p-6 text-sm text-muted-foreground">No users match.</div>}
      </div>

      {detail && (
        <div data-testid="user-details-drawer" className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetail(null)}/>
          <div className="relative bg-background w-full max-w-lg h-full overflow-y-auto border-l border-foreground/15">
            <div className="sticky top-0 bg-background border-b border-foreground/15 flex items-center justify-between p-5 z-10">
              <div className="overline flex items-center gap-2">◆ SELLER DETAILS</div>
              <button data-testid="user-details-close" onClick={() => setDetail(null)} className="p-1 hover:bg-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                {detail.picture ? <img src={detail.picture} className="w-16 h-16 rounded-full object-cover border border-foreground/10" alt=""/> : <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">{detail.name?.[0]}</div>}
                <div className="min-w-0">
                  <div className="font-semibold text-lg truncate">{detail.name}</div>
                  <div className="mono text-xs opacity-70 truncate">{detail.user_id}</div>
                </div>
              </div>

              <DetailRow label="Email" value={detail.email} onCopy={() => copy(detail.email, "Email copied")}/>
              <DetailRow label="Mobile" value={detail.mobile || "—"} onCopy={detail.mobile ? () => copy(detail.mobile, "Mobile copied") : undefined}/>
              <DetailRow label="Address" value={detail.address || "—"} multiline/>
              <DetailRow label="Role" value={(detail.role || "seller").toUpperCase()}/>
              <DetailRow label="Rating" value={`${detail.rating_avg?.toFixed(1) || "0.0"} (${detail.rating_count || 0})`}/>

              <div className="border border-foreground/15 p-4">
                <div className="overline mb-3">◆ Bank details</div>
                {detail.bank && (detail.bank.account_number || detail.bank.account_holder) ? (
                  <div className="space-y-2 text-sm">
                    <BankRow label="Holder" value={detail.bank.account_holder} onCopy={() => copy(detail.bank.account_holder)}/>
                    <BankRow label="Bank" value={detail.bank.bank_name} onCopy={() => copy(detail.bank.bank_name)}/>
                    <BankRow label="Account" value={detail.bank.account_number} mono onCopy={() => copy(detail.bank.account_number, "Account # copied")}/>
                    <BankRow label="IFSC" value={detail.bank.ifsc} mono onCopy={() => copy(detail.bank.ifsc, "IFSC copied")}/>
                    <button
                      data-testid="user-details-copy-all-bank"
                      onClick={() => copy(
                        `${detail.bank.account_holder} · ${detail.bank.bank_name}\nA/C: ${detail.bank.account_number}\nIFSC: ${detail.bank.ifsc}`,
                        "All bank details copied"
                      )}
                      className="w-full mt-3 mono text-xs bg-foreground text-background px-4 py-2 hover:bg-foreground/90"
                    >COPY ALL BANK DETAILS</button>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">No bank details on file.</div>
                )}
              </div>

              <div className="border border-foreground/15 p-4">
                <div className="overline mb-3">◆ Government ID</div>
                {detail.identity && detail.identity.id_number ? (
                  <div className="space-y-2 text-sm">
                    <BankRow label="Type" value={(detail.identity.id_type || "").toUpperCase()}/>
                    <BankRow label="Number" value={detail.identity.id_number} mono onCopy={() => copy(detail.identity.id_number, "ID # copied")}/>
                    {detail.identity.doc_path && (
                      <a
                        data-testid="user-details-id-doc"
                        href={fileUrl(detail.identity.doc_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="block border border-foreground/20 hover:border-foreground mt-2"
                      >
                        <img src={fileUrl(detail.identity.doc_path)} alt="ID doc" className="w-full aspect-[3/2] object-cover"/>
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">No ID document on file.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, multiline, onCopy }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-foreground/10 pb-2">
      <div className="overline text-xs opacity-70">{label}</div>
      <div className={`text-sm text-right ${multiline ? "whitespace-pre-wrap" : "truncate"} flex-1 min-w-0`}>{value}</div>
      {onCopy && <button onClick={onCopy} className="mono text-[10px] text-primary hover:underline whitespace-nowrap">COPY</button>}
    </div>
  );
}
function BankRow({ label, value, mono, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="overline text-xs opacity-70 min-w-[70px]">{label}</div>
      <div className={`text-sm text-right flex-1 truncate ${mono ? "mono" : ""}`}>{value || "—"}</div>
      {onCopy && value && <button onClick={onCopy} className="mono text-[10px] text-primary hover:underline whitespace-nowrap">COPY</button>}
    </div>
  );
}

function GigsPanel() {
  const [gigs, setGigs] = useState([]);
  const [q, setQ] = useState("");
  const load = () => adminGigs({ q: q || undefined }).then(setGigs).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const toggle = async (g, key) => {
    try {
      await adminUpdateGig(g.gig_id, { [key]: !g[key] });
      toast.success("Updated");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Input data-testid="admin-gigs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search gigs" className="mb-4 rounded-none max-w-md border-foreground/20"/>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {gigs.map((g) => (
          <div key={g.gig_id} data-testid={`admin-gig-${g.gig_id}`} className="border border-foreground/15 bg-card p-4">
            {g.cover_image && <img src={fileUrl(g.cover_image)} className="aspect-video w-full object-cover mb-3 border border-foreground/10" alt=""/>}
            <Link to={`/gig/${g.gig_id}`} className="font-medium line-clamp-2 hover:text-primary text-sm">{g.title}</Link>
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 mb-3">
              <span>{g.seller_name}</span>
              <span className="mono">{g.orders_count} orders · {g.rating_avg?.toFixed(1) || "—"}</span>
            </div>
            <div className="flex gap-2 border-t border-foreground/10 pt-3">
              <button
                data-testid={`admin-gig-feature-${g.gig_id}`}
                onClick={() => toggle(g, "featured")}
                className={`mono text-xs px-2 py-1.5 flex-1 ${g.featured ? "bg-primary text-primary-foreground" : "border border-foreground/30"}`}
              >{g.featured ? "★ FEATURED" : "FEATURE"}</button>
              <button
                data-testid={`admin-gig-toggle-${g.gig_id}`}
                onClick={() => toggle(g, "is_active")}
                className={`mono text-xs px-2 py-1.5 flex-1 ${g.is_active ? "border border-foreground/30" : "bg-destructive text-destructive-foreground"}`}
              >{g.is_active ? "DEACTIVATE" : "REACTIVATE"}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const load = () => adminOrders(status ? { status } : {}).then(setOrders).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const refund = async (o) => {
    if (!confirm(`Refund order ${o.order_id}?`)) return;
    try { await adminRefundOrder(o.order_id); toast.success("Refunded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["", "pending_payment", "in_progress", "delivered", "completed", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`admin-orders-filter-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`mono text-xs px-3 py-1.5 ${status === s ? "bg-foreground text-background" : "border border-foreground/30"}`}
          >{(s || "all").replace("_"," ").toUpperCase()}</button>
        ))}
      </div>
      <div className="border border-foreground/15">
        <div className="grid grid-cols-[1fr_120px_120px_120px_100px] gap-3 px-4 py-3 border-b border-foreground/15 bg-muted overline">
          <div>◆ Gig</div><div>Buyer</div><div>Price</div><div>Status</div><div>Action</div>
        </div>
        {orders.map((o) => (
          <div key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="grid grid-cols-[1fr_120px_120px_120px_100px] gap-3 px-4 py-3 border-b border-foreground/10 items-center text-sm">
            <Link to={`/order/${o.order_id}`} className="font-medium hover:text-primary line-clamp-1">{o.gig_title}</Link>
            <div className="text-muted-foreground text-xs truncate">{o.buyer_name}</div>
            <div className="mono">${o.package_price}</div>
            <div><span className="text-xs mono uppercase px-2 py-1 border border-foreground/40">{o.status.replace("_"," ")}</span></div>
            <button
              data-testid={`admin-refund-${o.order_id}`}
              onClick={() => refund(o)}
              disabled={o.payment_status !== "paid" || o.status === "cancelled"}
              className="mono text-xs border border-destructive text-destructive px-2 py-1.5 hover:bg-destructive hover:text-destructive-foreground disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-destructive"
            >REFUND</button>
          </div>
        ))}
        {orders.length === 0 && <div className="p-6 text-sm text-muted-foreground">No orders.</div>}
      </div>
    </div>
  );
}

function WithdrawalsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [paying, setPaying] = useState(null);   // withdrawal_id being marked paid
  const [utr, setUtr] = useState("");
  const load = () => adminWithdrawals(status ? { status } : {}).then(setItems).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSimple = async (w, s) => {
    try { await adminUpdateWithdrawal(w.withdrawal_id, { status: s }); toast.success(s); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markPaid = async () => {
    if (!utr.trim()) { toast.error("Enter UTR / reference"); return; }
    try {
      await adminUpdateWithdrawal(paying.withdrawal_id, { status: "paid", utr_number: utr.trim() });
      toast.success("Payout confirmed");
      setPaying(null); setUtr("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending","approved","paid","rejected",""].map((s) => (
          <button
            key={s || "all"}
            data-testid={`admin-wd-filter-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`mono text-xs px-3 py-1.5 ${status === s ? "bg-foreground text-background" : "border border-foreground/30"}`}
          >{(s || "all").toUpperCase()}</button>
        ))}
      </div>
      <div className="border border-foreground/15 overflow-x-auto">
        <div className="grid grid-cols-[1fr_90px_90px_90px_240px_120px_100px_190px] gap-3 px-4 py-3 border-b border-foreground/15 bg-muted overline min-w-[1200px]">
          <div>◆ Seller</div><div>Gross</div><div>Fee</div><div>Net</div><div>Bank details</div><div>UTR / Ref</div><div>Status</div><div>Action</div>
        </div>
        {items.map((w) => (
          <div key={w.withdrawal_id} data-testid={`admin-wd-${w.withdrawal_id}`} className="grid grid-cols-[1fr_90px_90px_90px_240px_120px_100px_190px] gap-3 px-4 py-3 border-b border-foreground/10 items-start text-sm min-w-[1200px]">
            <div className="min-w-0">
              <div className="font-medium truncate">{w.seller_name || w.seller_id}</div>
              <div className="mono text-[10px] text-muted-foreground truncate">{w.seller_email || w.seller_id}</div>
            </div>
            <div className="mono">${(w.gross_amount ?? w.amount ?? 0).toFixed(2)}</div>
            <div className="mono text-destructive">−${(w.platform_fee ?? 0).toFixed(2)}</div>
            <div className="mono font-semibold">${(w.net_amount ?? w.amount ?? 0).toFixed(2)}</div>
            <div className="mono text-[11px] break-all" title={w.bank_snapshot?.account_number || ""}>
              {w.bank_snapshot?.bank_name ? (
                <>
                  <div className="truncate">{w.bank_snapshot.bank_name}</div>
                  <div data-testid={`admin-wd-account-${w.withdrawal_id}`}>A/C: {w.bank_snapshot.account_number || "—"}</div>
                  <div className="opacity-70">IFSC: {w.bank_snapshot.ifsc || "—"}</div>
                  <div className="opacity-70 truncate">{w.bank_snapshot.account_holder || "—"}</div>
                </>
              ) : (w.destination || w.method)}
            </div>
            <div className="mono text-[11px] truncate" title={w.utr_number || ""} data-testid={`admin-wd-utr-${w.withdrawal_id}`}>
              {w.utr_number || <span className="opacity-40">—</span>}
            </div>
            <div>
              <span className={`text-xs mono uppercase px-2 py-1 border ${w.status === "paid" ? "border-primary text-primary bg-primary/10" : "border-foreground/40"}`}>
                {w.status}
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {w.status === "pending" && (
                <>
                  <button data-testid={`admin-wd-approve-${w.withdrawal_id}`} onClick={() => setSimple(w, "approved")} className="mono text-xs bg-primary text-primary-foreground px-2 py-1.5 hover:bg-primary/90">APPROVE</button>
                  <button data-testid={`admin-wd-reject-${w.withdrawal_id}`} onClick={() => setSimple(w, "rejected")} className="mono text-xs border border-destructive text-destructive px-2 py-1.5">REJECT</button>
                </>
              )}
              {w.status === "approved" && (
                <button data-testid={`admin-wd-mark-paid-${w.withdrawal_id}`} onClick={() => { setPaying(w); setUtr(""); }} className="mono text-xs bg-foreground text-background px-2 py-1.5 hover:bg-foreground/90">MARK PAID</button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-sm text-muted-foreground">No withdrawals.</div>}
      </div>

      {paying && (
        <div data-testid="mark-paid-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 border border-foreground/15">
            <div className="overline mb-1">◆ CONFIRM PAYOUT</div>
            <h3 className="text-xl font-bold mb-4">Mark payout as PAID</h3>
            <div className="text-sm space-y-1 mb-4">
              <div><span className="text-muted-foreground">Seller:</span> <b>{paying.seller_name}</b> <span className="opacity-60 text-xs">({paying.seller_email})</span></div>
              <div><span className="text-muted-foreground">Net paid:</span> <b className="mono">${(paying.net_amount ?? paying.amount).toFixed(2)}</b></div>
              <div className="mt-3 border border-foreground/15 p-3 bg-muted/40 mono text-xs space-y-0.5">
                <div className="overline text-[10px] opacity-60 mb-1">◆ SEND TO</div>
                <div>Bank: <b>{paying.bank_snapshot?.bank_name || "—"}</b></div>
                <div>Holder: {paying.bank_snapshot?.account_holder || "—"}</div>
                <div>A/C: <b data-testid="mark-paid-account">{paying.bank_snapshot?.account_number || "—"}</b></div>
                <div>IFSC: {paying.bank_snapshot?.ifsc || "—"}</div>
              </div>
            </div>
            <label className="overline block mb-2">◆ UTR / Reference number *</label>
            <input
              data-testid="mark-paid-utr-input"
              value={utr}
              onChange={(e) => setUtr(e.target.value.toUpperCase())}
              placeholder="e.g. UTR123456789"
              className="w-full border border-foreground/20 px-3 py-2 rounded-none mono text-sm uppercase focus:outline-none focus:border-primary"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="mark-paid-cancel" onClick={() => { setPaying(null); setUtr(""); }} className="mono text-xs border border-foreground/30 px-4 py-2">CANCEL</button>
              <button data-testid="mark-paid-confirm" onClick={markPaid} disabled={!utr.trim()} className="mono text-xs bg-primary text-primary-foreground px-4 py-2 disabled:opacity-40">CONFIRM PAID</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const load = () => adminReviews().then(setReviews).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => {
    if (!confirm("Delete this review? Ratings will be recomputed.")) return;
    try { await adminDeleteReview(r.review_id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {reviews.map((r) => (
        <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="border border-foreground/15 bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="font-medium text-sm">{r.buyer_name}</div>
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-primary text-primary" : "text-muted"}`}/>)}
            </div>
          </div>
          <p className="text-sm text-foreground/80 line-clamp-3 mb-3">{r.comment}</p>
          <div className="flex items-center justify-between border-t border-foreground/10 pt-3">
            <Link to={`/gig/${r.gig_id}`} className="mono text-xs text-muted-foreground hover:text-primary truncate">{r.gig_id}</Link>
            <button data-testid={`admin-delete-review-${r.review_id}`} onClick={() => del(r)} className="mono text-xs flex items-center gap-1 text-destructive border border-destructive px-2 py-1 hover:bg-destructive hover:text-destructive-foreground">
              <Trash2 className="w-3 h-3"/> DELETE
            </button>
          </div>
        </div>
      ))}
      {reviews.length === 0 && <div className="p-6 text-sm text-muted-foreground">No reviews yet.</div>}
    </div>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { fetchAnalytics(days).then(setData).catch(() => {}); }, [days]);
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading analytics…</div>;

  const max = Math.max(...data.timeline.map((d) => d.gmv), 1);

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            data-testid={`admin-analytics-${d}d`}
            onClick={() => setDays(d)}
            className={`mono text-xs px-3 py-1.5 ${days === d ? "bg-foreground text-background" : "border border-foreground/30"}`}
          >LAST {d}D</button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat testid="an-gmv" icon={<DollarSign className="w-5 h-5"/>} label="GMV" value={`$${data.totals.gmv.toLocaleString()}`} highlight/>
        <Stat testid="an-orders" icon={<ShoppingBag className="w-5 h-5"/>} label="Paid orders" value={data.totals.orders}/>
        <Stat testid="an-users" icon={<Users className="w-5 h-5"/>} label="New users" value={data.totals.new_users}/>
      </div>

      {/* GMV timeline sparkbar */}
      <div className="border border-foreground/15 p-6 bg-card">
        <div className="overline mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4"/> ◆ GMV by day</div>
        {data.timeline.length === 0 ? (
          <div className="text-sm text-muted-foreground">No paid orders in this window.</div>
        ) : (
          <div className="flex items-end gap-1 h-40" data-testid="analytics-timeline">
            {data.timeline.map((d) => (
              <div key={d.date} className="flex-1 group relative flex flex-col items-center">
                <div
                  className="w-full bg-foreground group-hover:bg-primary transition-colors"
                  style={{ height: `${Math.max(2, (d.gmv / max) * 100)}%` }}
                  title={`${d.date}: $${d.gmv.toFixed(2)} · ${d.orders} orders`}
                />
              </div>
            ))}
          </div>
        )}
        <div className="mono text-[10px] text-muted-foreground mt-2 flex justify-between">
          <span>{data.timeline[0]?.date}</span>
          <span>{data.timeline[data.timeline.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-foreground/15 p-6 bg-card">
          <div className="overline mb-4">◆ Top sellers</div>
          {data.top_sellers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No data.</div>
          ) : data.top_sellers.map((s, i) => (
            <div key={s.seller_id} data-testid={`an-top-seller-${i}`} className="flex items-center justify-between py-2 border-b border-foreground/5 last:border-b-0">
              <div className="flex items-center gap-3">
                <span className="mono text-xs w-6 text-muted-foreground">#{i+1}</span>
                <span className="font-medium text-sm">{s.seller_name}</span>
              </div>
              <div className="text-right">
                <div className="mono font-semibold text-sm">${s.gmv.toLocaleString()}</div>
                <div className="mono text-[10px] text-muted-foreground">{s.orders} orders</div>
              </div>
            </div>
          ))}
        </div>
        <div className="border border-foreground/15 p-6 bg-card">
          <div className="overline mb-4">◆ Top categories</div>
          {data.top_categories.length === 0 ? (
            <div className="text-sm text-muted-foreground">No data.</div>
          ) : data.top_categories.map((c, i) => (
            <div key={c.category} data-testid={`an-top-cat-${i}`} className="flex items-center justify-between py-2 border-b border-foreground/5 last:border-b-0">
              <div className="flex items-center gap-3">
                <span className="mono text-xs w-6 text-muted-foreground">#{i+1}</span>
                <span className="font-medium text-sm capitalize">{c.category}</span>
              </div>
              <div className="text-right">
                <div className="mono font-semibold text-sm">${c.gmv.toLocaleString()}</div>
                <div className="mono text-[10px] text-muted-foreground">{c.orders} orders</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VerificationsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => adminListVerifications(status).then(setItems).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (v, decision) => {
    try {
      await adminDecideVerification(v.verification_id, decision, "");
      toast.success(`${decision}`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending","approved","rejected"].map((s) => (
          <button
            key={s}
            data-testid={`admin-ver-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`mono text-xs px-3 py-1.5 ${status === s ? "bg-foreground text-background" : "border border-foreground/30"}`}
          >{s.toUpperCase()}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground border border-dashed border-foreground/20">No {status} verifications.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((v) => (
            <div key={v.verification_id} data-testid={`admin-ver-${v.verification_id}`} className="border border-foreground/15 bg-card p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-semibold flex items-center gap-1.5">
                    {v.user_name}
                    {v.status === "approved" && <BadgeCheck className="w-4 h-4 fill-primary text-primary-foreground"/>}
                  </div>
                  <div className="mono text-xs text-muted-foreground">{v.user_email}</div>
                </div>
                <span className={`text-xs mono uppercase px-2 py-1 border ${v.status === "pending" ? "border-foreground/40" : v.status === "approved" ? "border-primary text-primary" : "border-destructive text-destructive"}`}>{v.status}</span>
              </div>
              <div className="text-sm space-y-1 border-t border-foreground/10 pt-3">
                <div><span className="mono text-xs opacity-60">ID TYPE</span> — {v.id_type}</div>
                <div><span className="mono text-xs opacity-60">ID NUMBER</span> — {v.id_number}</div>
                {v.doc_path && (
                  <div className="mt-3">
                    <div className="mono text-xs opacity-60 mb-1.5">ID DOCUMENT</div>
                    <a
                      href={fileUrl(v.doc_path)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-ver-doc-${v.verification_id}`}
                      className="block border border-foreground/20 hover:border-foreground transition-colors"
                    >
                      <img src={fileUrl(v.doc_path)} alt="ID document" className="w-full aspect-[3/2] object-cover"/>
                    </a>
                  </div>
                )}
                {v.notes && <div className="text-muted-foreground text-xs mt-2">{v.notes}</div>}
              </div>
              {v.status === "pending" && (
                <div className="flex gap-2 mt-4 border-t border-foreground/10 pt-3">
                  <button data-testid={`admin-ver-approve-${v.verification_id}`} onClick={() => decide(v, "approved")} className="mono text-xs bg-primary text-primary-foreground px-3 py-1.5 flex-1 hover:bg-primary/90">APPROVE</button>
                  <button data-testid={`admin-ver-reject-${v.verification_id}`} onClick={() => decide(v, "rejected")} className="mono text-xs border border-destructive text-destructive px-3 py-1.5 flex-1 hover:bg-destructive hover:text-destructive-foreground">REJECT</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementsPanel() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = () => adminListAnnouncements().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async () => {
    if (!text.trim()) return;
    try {
      await adminCreateAnnouncement(text.trim(), true);
      toast.success("Announcement live");
      setText("");
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (a) => {
    if (!confirm("Delete this announcement?")) return;
    try { await adminDeleteAnnouncement(a.announcement_id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="border border-foreground/15 bg-card p-5 mb-6">
        <div className="overline mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4"/> ◆ Post a sitewide banner</div>
        <Textarea
          data-testid="admin-ann-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Holiday sale — 20% off all gigs this weekend."
          rows={2}
          maxLength={280}
          className="rounded-none border-foreground/20 mb-3"
        />
        <div className="flex items-center justify-between">
          <div className="mono text-xs text-muted-foreground">{text.length}/280 · Posting will hide any previous banner</div>
          <button
            data-testid="admin-ann-post"
            onClick={post}
            disabled={!text.trim()}
            className="mono text-xs bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90 disabled:opacity-40"
          >PUBLISH</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground border border-dashed border-foreground/20">No announcements yet.</div>
      ) : items.map((a) => (
        <div key={a.announcement_id} data-testid={`admin-ann-${a.announcement_id}`} className="border border-foreground/15 bg-card p-4 mb-3 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {a.active && <span className="mono text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5">LIVE</span>}
              <span className="mono text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm">{a.text}</div>
          </div>
          <button data-testid={`admin-ann-delete-${a.announcement_id}`} onClick={() => del(a)} className="mono text-xs border border-destructive text-destructive px-2 py-1 hover:bg-destructive hover:text-destructive-foreground flex items-center gap-1">
            <X className="w-3 h-3"/> DELETE
          </button>
        </div>
      ))}
    </div>
  );
}


function DisputesPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const [note, setNote] = useState({});
  const load = () => adminListDisputes(status).then(setItems).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (d, resolution) => {
    if (!confirm(`${resolution === "refund" ? "Refund" : "Reject"} this dispute?`)) return;
    try {
      await adminResolveDispute(d.dispute_id, resolution, note[d.dispute_id] || "");
      toast.success(resolution === "refund" ? "Refunded" : "Rejected");
      setNote((n) => ({ ...n, [d.dispute_id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "resolved_refund", "resolved_rejected", ""].map((s) => (
          <button
            key={s || "all"}
            data-testid={`admin-disp-filter-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`mono text-xs px-3 py-1.5 ${status === s ? "bg-foreground text-background" : "border border-foreground/30"}`}
          >{(s || "all").replace("_", " ").toUpperCase()}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground border border-dashed border-foreground/20">
          No {status ? status.replace("_", " ") : ""} disputes.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((d) => (
            <div key={d.dispute_id} data-testid={`admin-disp-${d.dispute_id}`} className="border border-foreground/15 bg-card p-5">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                  <Link to={`/order/${d.order_id}`} className="font-semibold hover:text-primary line-clamp-1">{d.gig_title}</Link>
                  <div className="mono text-xs text-muted-foreground mt-1">
                    {d.buyer_name} ↔ {d.seller_name} · ${d.amount?.toFixed?.(2) || d.amount}
                  </div>
                </div>
                <span className={`text-xs mono uppercase px-2 py-1 border whitespace-nowrap ${
                  d.status === "open" ? "border-destructive text-destructive"
                    : d.status === "resolved_refund" ? "border-primary text-primary"
                    : "border-foreground/40"
                }`}>{d.status.replace("_", " ")}</span>
              </div>
              <div className="border-t border-foreground/10 pt-3 text-sm mb-2">
                <div className="mono text-xs opacity-60 mb-1">RAISED BY {d.raised_by?.toUpperCase()}</div>
                <p className="whitespace-pre-wrap">{d.reason}</p>
              </div>
              {d.admin_note && (
                <div className="mt-2 border-t border-foreground/10 pt-2 text-xs">
                  <span className="mono text-muted-foreground">ADMIN NOTE: </span>{d.admin_note}
                </div>
              )}
              {d.status === "open" && (
                <div className="border-t border-foreground/10 pt-3 mt-3 space-y-2">
                  <Textarea
                    data-testid={`admin-disp-note-${d.dispute_id}`}
                    value={note[d.dispute_id] || ""}
                    onChange={(e) => setNote((n) => ({ ...n, [d.dispute_id]: e.target.value }))}
                    placeholder="Optional admin note to both parties"
                    rows={2}
                    className="rounded-none border-foreground/20 text-xs"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-disp-refund-${d.dispute_id}`}
                      onClick={() => resolve(d, "refund")}
                      className="mono text-xs bg-primary text-primary-foreground px-3 py-1.5 flex-1 hover:bg-primary/90"
                    >REFUND BUYER</button>
                    <button
                      data-testid={`admin-disp-reject-${d.dispute_id}`}
                      onClick={() => resolve(d, "reject")}
                      className="mono text-xs border border-destructive text-destructive px-3 py-1.5 flex-1 hover:bg-destructive hover:text-destructive-foreground"
                    >REJECT DISPUTE</button>
                  </div>
                </div>
              )}
              <DisputeChat dispute={d} testIdPrefix={`admin-disp-chat-${d.dispute_id}`}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

